import { TPopupProps } from '@@types/popup';
import {
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  Stack,
} from '@chakra-ui/react';
import Content from '@components/content';
import currency from '@constants/currency';
import useOrder from '@hooks/useOrder';
import { ICoupon } from 'interfaces/coupon';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { IoMdCheckmarkCircle } from 'react-icons/io';
import { setComma } from 'utils/common';

export default function CouponDrawer({
  props,
  closePopup,
}: TPopupProps<{
  coupons: ICoupon[];
  margin: number;
  currentCoupon?: string;
}>) {
  const { coupons, currentCoupon } = props;
  const { order, updateOrderInfo } = useOrder();
  const { t } = useTranslation('order');

  const calculateDaysUntilExpiry = (expiryDateStr: string) => {
    const now = DateTime.now();
    const expiryDate = DateTime.fromISO(expiryDateStr);

    const daysRemaining = expiryDate.diff(now, 'days').days;
    if (daysRemaining > 0) {
      return `D-${Math.ceil(daysRemaining)}`;
    } else if (daysRemaining === 0) {
      return '오늘';
    } else {
      return '만료됨';
    }
  };

  const onSelectCoupon = (coupon: ICoupon) => {
    if (!order.cart) return closePopup();
    if (!!order.coupon && order.coupon._id === currentCoupon) {
      updateOrderInfo({ coupon: undefined });
      return closePopup();
    }

    updateOrderInfo({
      coupon: {
        ...coupon,
      },
    });

    return closePopup();
  };

  return (
    <DrawerContent className="rounded-t-2xl">
      <Content>
        <DrawerHeader>{t('store:owned_coupons')}</DrawerHeader>
        <DrawerCloseButton />
      </Content>
      <DrawerBody className="max-h-[60vh] overflow-y-auto p-0 pb-6">
        <Content>
          <Stack spacing="12px">
            {coupons.length > 0 ? (
              coupons.map((coupon) => (
                <div
                  key={`coupon/${coupon._id}`}
                  className={`cursor-pointer relative `}
                  onClick={() => onSelectCoupon(coupon)}
                >
                  {coupon._id === currentCoupon && (
                    <div className="absolute top-2 left-2 z-10">
                      <IoMdCheckmarkCircle size="32" color="#156b7e" />
                    </div>
                  )}
                  <div
                    className={`w-full flex h-[120px] ${
                      coupon._id === currentCoupon ? 'opacity-30' : ''
                    }`}
                  >
                    <div className="relative basis-4/12 text-center py-2 border-t-2 border-b-2 border-l-2 rounded-xl flex items-center justify-center bg-gray-100">
                      <h2 className="text-xl font-semibold text-primary-700">
                        {setComma(coupon.amount)}
                        {currency.kr}
                      </h2>
                      <div className="absolute top-0 right-0 w-full h-full bg-transparent rounded-xl border-r-2 border-dashed"></div>
                    </div>
                    <div className="basis-8/12 border-t-2 border-b-2 border-r-2 rounded-xl">
                      <div className="py-2 text-center flex flex-col justify-between h-full">
                        <p className="mb-2 flex items-center justify-center flex-1 font-bold text-primary-500 text-md px-2">
                          {coupon.description}
                        </p>
                        <div className="font-bold text-sm">
                          <p className="text-xs">
                            <strong>사용처: </strong>
                            {coupon.usablePlatform === 'MARKET'
                              ? 'MarketPanther'
                              : coupon.usablePlatform === 'FOOD'
                              ? 'FoodPanther'
                              : 'MarketPanther, FoodPanther'}
                          </p>
                          <p>
                            {coupon.expirationDate && (
                              <>
                                <strong>~</strong>{' '}
                                <>
                                  {DateTime.fromISO(coupon.expirationDate)
                                    .setZone('Asia/Seoul')
                                    .toFormat('yyyy-MM-dd')}
                                </>
                                <>
                                  {'('}
                                  {calculateDaysUntilExpiry(
                                    coupon.expirationDate,
                                  )}
                                  {')'}
                                </>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      {/* <div className="absolute top-1/2 -left-5 w-10 h-10 bg-white rounded-full transform -translate-y-1/2 shadow-lg"></div>
                  <div className="absolute top-1/2 -right-5 w-10 h-10 bg-white rounded-full transform -translate-y-1/2 shadow-lg"></div> */}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div>
                <p>{t('no_available_coupons')}</p>
              </div>
            )}
          </Stack>
        </Content>
      </DrawerBody>
      <DrawerFooter />
    </DrawerContent>
  );
}
